import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authButtonClass, authErrorClass } from './AuthCard';

const EmailVerificationCard = ({ email, onSuccess, onCancel, initialMessage }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [infoMsg, setInfoMsg] = useState(initialMessage || '');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(60);
  const inputRef = useRef(null);

  const { verifyEmail, resendVerification } = useAuth();

  // Focus input automatically on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleCodeChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(val);
    setError('');
  };

  const handleVerify = async (e) => {
    if (e) e.preventDefault();
    if (code.length < 6) {
      setError('Please enter all 6 digits of your verification code.');
      return;
    }

    setError('');
    setInfoMsg('');
    setVerifying(true);

    const result = await verifyEmail(email, code);

    if (result.success) {
      if (onSuccess) {
        onSuccess();
      }
    } else {
      setError(result.error || 'Invalid or expired code. Please try again.');
    }
    setVerifying(false);
  };

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    setError('');
    setInfoMsg('');

    const result = await resendVerification(email);

    if (result.success) {
      setInfoMsg(result.message || 'A fresh verification code has been sent to your email.');
      setCooldown(60);
      setCode('');
    } else {
      setError(result.error || 'Failed to resend code. Please try again.');
    }
    setResending(false);
  };

  return (
    <div className="space-y-4">
      {/* Icon header */}
      <div className="flex justify-center mb-1">
        <div className="w-14 h-14 rounded-2xl bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-900/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center text-2xl shadow-xs">
          <i className="fas fa-envelope-open-text" />
        </div>
      </div>

      <div className="text-center">
        <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">
          Verify your email
        </h2>
        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 leading-relaxed">
          We sent a 6-digit confirmation code to:
          <br />
          <strong className="text-zinc-800 dark:text-zinc-200 font-semibold">{email}</strong>
        </p>
      </div>

      {error && (
        <div className={authErrorClass}>
          <i className="fas fa-circle-exclamation text-sm shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {infoMsg && !error && (
        <div className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900/60 text-emerald-700 dark:text-emerald-300 px-3.5 py-2.5 rounded-xl text-xs font-medium">
          <i className="fas fa-check-circle text-sm shrink-0 text-emerald-600 dark:text-emerald-400" />
          <span>{infoMsg}</span>
        </div>
      )}

      <form onSubmit={handleVerify} className="space-y-4 pt-1">
        <div>
          <label className="block text-xs font-semibold text-zinc-600 dark:text-zinc-400 mb-2 text-center uppercase tracking-wider">
            6-Digit Code
          </label>
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            autoComplete="one-time-code"
            value={code}
            onChange={handleCodeChange}
            placeholder="••••••"
            className="w-full py-3 px-4 rounded-xl border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 font-mono text-2xl font-black text-center tracking-[0.4em] focus:ring-2 focus:ring-indigo-500 focus:outline-hidden transition-all shadow-inner"
          />
          <p className="text-[11px] text-zinc-400 text-center mt-1.5">
            Check your inbox and spam / junk folder
          </p>
        </div>

        <button
          type="submit"
          disabled={verifying || code.length < 6}
          className={`${authButtonClass} !bg-indigo-600 hover:!bg-indigo-700 text-white`}
        >
          {verifying ? (
            <span className="flex items-center justify-center gap-2">
              <i className="fas fa-circle-notch fa-spin text-xs" />
              <span>Verifying...</span>
            </span>
          ) : (
            'Verify & Sign In'
          )}
        </button>
      </form>

      <div className="pt-2 flex flex-col items-center gap-2 text-xs">
        <div className="text-zinc-500 dark:text-zinc-400">
          Didn't get the code?{' '}
          {cooldown > 0 ? (
            <span className="text-zinc-400 font-medium">
              Resend in {cooldown}s
            </span>
          ) : (
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="text-indigo-600 dark:text-indigo-400 font-bold hover:underline disabled:opacity-50"
            >
              {resending ? 'Sending...' : 'Resend Code'}
            </button>
          )}
        </div>

        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors text-[11.5px] mt-1"
          >
            ← Back to account options
          </button>
        )}
      </div>
    </div>
  );
};

export default EmailVerificationCard;
